import * as React from "react";
import { AlertColor, MenuItem, Stack, TextField } from "@mui/material";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import { useAddPayRateMutation, useModifyPayRateMutation } from "../../../services/cclTokenedPayrollApi";
import { PayRate } from "../../../services/types/payrollEventTypes";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";

export interface AddEditFeeDrawerProps {
    open: boolean;
    mode?: "add" | "edit";
    fee?: PayRate | null;
    onClose: () => void;
    onSaveResult: (message: string, severity: AlertColor) => void;
}

const AddEditFeeDrawer: React.FC<AddEditFeeDrawerProps> = (props) => {
    const isEditMode = props.mode === "edit";
    const [formData, setFormData] = React.useState<Record<string, any>>({});

    const [addPayRate, { isLoading: isAddLoading }] = useAddPayRateMutation();
    const [modifyPayRate, { isLoading: isModifyLoading }] = useModifyPayRateMutation();
    const { logEvent } = useLogAccessEvent();

    React.useEffect(() => {
        if (!props.open) {
            return;
        }

        const nextData: Record<string, any> = {
            rateCode: props.fee?.rateCode ?? "",
            rateDesc: props.fee?.rateDesc ?? "",
            unit: props.fee?.unit ?? "",
            currentFee: props.fee?.currentFee ?? "",
            ultiproText: props.fee?.ultiproText ?? "",
            active: props.fee?.active ?? true,
        };

        setFormData(nextData);
        //eslint-disable-next-line
    }, [props.open, props.fee]);

    const isSaving = isAddLoading || isModifyLoading;

    const canSave = !isSaving && (formData.rateCode?.toString().trim() ?? "").length > 0;

    const handleFieldChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        const payRatePayload: PayRate = {
            rateCode: String(formData.rateCode ?? "").trim(),
            rateDesc: String(formData.rateDesc ?? "").trim(),
            unit: String(formData.unit ?? "").trim(),
            currentFee: Number(formData.currentFee ?? 0),
            ultiproText: String(formData.ultiproText ?? "").trim(),
            active: true,
        };

        const saveAction = isEditMode ? modifyPayRate(payRatePayload) : addPayRate(payRatePayload);

        saveAction
            .unwrap()
            .then(() => {
                logEvent(isEditMode ? "FeeEdited" : "FeeAdded", null);
                props.onSaveResult(isEditMode ? "Pay Rate Edited" : "Pay Rate Added", "success");
                props.onClose();
            })
            .catch((error) => {
                console.error("Error saving pay rate:", error);
                props.onSaveResult(
                    isEditMode ? "Failed to edit Pay Rate" : "Failed to add Pay Rate",
                    "error"
                );
            });
    };

    return (
        <CclDrawer title={isEditMode ? "Edit Pay Rate" : "Add Pay Rate"} open={props.open} onClose={props.onClose}>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                    id="add-edit-fee-rateCode"
                    label="Code"
                    size="small"
                    fullWidth
                    disabled={isEditMode}
                    value={formData.rateCode ?? ""}
                    inputProps={{ maxLength: 8 }}
                    onChange={(e) => handleFieldChange("rateCode", e.target.value)}
                />
                <TextField
                    id="add-edit-fee-rateDesc"
                    label="Description"
                    size="small"
                    fullWidth
                    value={formData.rateDesc ?? ""}
                    onChange={(e) => handleFieldChange("rateDesc", e.target.value)}
                />
                <TextField
                    id="add-edit-fee-unit"
                    label="Unit"
                    size="small"
                    fullWidth
                    value={formData.unit ?? ""}
                    onChange={(e) => handleFieldChange("unit", e.target.value)}
                />
                <TextField
                    id="add-edit-fee-currentFee"
                    label="Current Fee"
                    size="small"
                    fullWidth
                    inputProps={{ step: "0.01" }}
                    value={formData.currentFee ?? ""}
                    onChange={(e) => handleFieldChange("currentFee", e.target.value)}
                />
                <TextField
                    id="add-edit-fee-ultiproText"
                    label="Ultipro Text"
                    size="small"
                    fullWidth
                    value={formData.ultiproText ?? ""}
                    onChange={(e) => handleFieldChange("ultiproText", e.target.value)}
                />
                <CclDrawerActionBar>
                    <CclLoadingButton mode="primary" loading={isSaving} disabled={!canSave} onClick={handleSave}>
                        Save
                    </CclLoadingButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Stack>
        </CclDrawer>
    );
};

export default AddEditFeeDrawer;
